import * as Switch from '@radix-ui/react-switch'
import * as Toast from '@radix-ui/react-toast'
import * as ToggleGroup from '@radix-ui/react-toggle-group'

import './styles.scss'
import { SelectablePreview } from '../selectable-preview/selectable-preview'
import { useSelector } from 'react-redux'
import { RootState, useAppDispatch } from '@AppState/redux-store'
import { ANIMATION_IDENTIFIERS } from '@Animation/types'
import { AppSettingsActions, GAME_SPEED } from '@AppState/app-settings'
import { useCallback, useEffect, useState } from 'react'
import { DrawerState, ExpandableDrawer } from '../expandable-drawer'
import { Utils } from '@Util/utils'
import { LoadingAnimation } from '@Components/loading/loading'
import { KenoDataServiceAPI } from '@API/gambyt-data-services/keno-api.ts'
import CarouselGroup from '@Components/carousel-group/carousel-group'

const CLASS_NAME = 'gby-k2g-settings'

export type SettingsProps = {
  drawerState: DrawerState
  onOpenChange: (open: boolean) => void
}

export const Settings = (props: SettingsProps) => {
  const { version } = useSelector((state: RootState) => state.appVersion)
  const { appSettings } = useSelector((state: RootState) => state.appSettings)
  const appConfig = useSelector((state: RootState) => state.appConfig)
  const hostConfig = useSelector((state: RootState) => state.hostConfig)
  const [toastVisible, setToastVisible] = useState<boolean>(false)

  const dispatch = useAppDispatch()

  let allAnimsSelected = true
  appSettings.animationsEnabled.forEach((anim) => {
    if (!anim.isEnabled) {
      allAnimsSelected = false
    }
  })

  useEffect(() => {
    if (props.drawerState === DrawerState.CLOSING) {
      KenoDataServiceAPI.savePreferences(
        appSettings,
        appConfig.config?.clientId,
        appConfig.config?.playerId,
      )
    }
  }, [appConfig, appSettings, props.drawerState])

  const showToast = useCallback(() => {
    setToastVisible(false)
    setTimeout(() => setToastVisible(true), 100)
  }, [])

  const onAnimationSelect = useCallback(
    (identifier: ANIMATION_IDENTIFIERS) => {
      const newAnimationsEnabled = appSettings.animationsEnabled.map((anim) => {
        if (anim.identifier === identifier) {
          return {
            identifier: identifier,
            isEnabled: !anim.isEnabled,
          }
        }
        return anim
      })
      dispatch(AppSettingsActions.updateAnimationsEnabled(newAnimationsEnabled))
    },
    [appSettings.animationsEnabled, dispatch],
  )

  const generateAnimationPreviews = useCallback(() => {
    return appSettings.animationsEnabled.map((animation) => {
      return (
        <SelectablePreview
          key={animation.identifier}
          imageUrl={`/animations/${Utils.toKebabCase(animation.identifier)}/settings-preview.png`}
          text={animation.identifier}
          isSelected={animation.isEnabled}
          onSelect={() => {
            onAnimationSelect(animation.identifier)
            showToast()
          }}
        />
      )
    })
  }, [appSettings.animationsEnabled, onAnimationSelect, showToast])

  const selectAll = useCallback(() => {
    if (allAnimsSelected) {
      const newAnimationsEnabled = appSettings.animationsEnabled.map((anim) => {
        return {
          identifier: anim.identifier,
          isEnabled: false,
        }
      })
      dispatch(AppSettingsActions.updateAnimationsEnabled(newAnimationsEnabled))
      return
    }
    const newAnimationsEnabled = appSettings.animationsEnabled.map((anim) => {
      return {
        identifier: anim.identifier,
        isEnabled: true,
      }
    })
    dispatch(AppSettingsActions.updateAnimationsEnabled(newAnimationsEnabled))
  }, [dispatch, appSettings, allAnimsSelected])

  if (hostConfig.config === null) {
    return <LoadingAnimation />
  }

  if (props.drawerState === DrawerState.CLOSED) {
    return null
  }

  return (
    <ExpandableDrawer
      classPrefix={CLASS_NAME}
      onOpenChange={props.onOpenChange}
      hasCloseButton={true}
      state={props.drawerState}>
      <Toast.Provider>
        <Toast.Root
          open={toastVisible}
          onOpenChange={setToastVisible}
          className={`${CLASS_NAME}__toast appear`}>
          <Toast.Description>
            <div onClick={() => setToastVisible(false)}>✅ Changes saved</div>
          </Toast.Description>
        </Toast.Root>
        {toastVisible ? ( // This conditional rendering is a hack but without it, you'll have a bad tme if you swipe to dismiss a toast
          <Toast.Viewport className={`${CLASS_NAME}__toast-container appear`} />
        ) : null}
      </Toast.Provider>
      {hostConfig.config?.settings.hasSound ? (
        <div className={`${CLASS_NAME}__option`}>
          <label className={`${CLASS_NAME}__label`} htmlFor={'sound-switch'}>
            Sound
          </label>
          <Switch.Root
            className={`${CLASS_NAME}__switch-root`}
            id="sound-switch"
            checked={appSettings.soundEnabled}
            onCheckedChange={(checked: boolean) => {
              dispatch(AppSettingsActions.updateSound(checked))
              showToast()
            }}>
            <Switch.Thumb className={`${CLASS_NAME}__switch-thumb`} />
          </Switch.Root>{' '}
        </div>
      ) : null}
      <div className={`${CLASS_NAME}__option`}>
        <label className={`${CLASS_NAME}__label`} htmlFor={'autoplay-switch'}>
          Auto-play
        </label>
        <Switch.Root
          className={`${CLASS_NAME}__switch-root`}
          id="autoplay-switch"
          checked={appSettings.autoPlay}
          onCheckedChange={(checked: boolean) => {
            dispatch(AppSettingsActions.updateAutoplay(checked))
            showToast()
          }}>
          <Switch.Thumb className={`${CLASS_NAME}__switch-thumb`} />
        </Switch.Root>{' '}
      </div>
      <div className={`${CLASS_NAME}__option`}>
        <div className={`${CLASS_NAME}__multiline-option`}>
          <label className={`${CLASS_NAME}__label`} htmlFor={'autoplay-switch'}>
            Game Speed
          </label>
          <ToggleGroup.Root
            className={`${CLASS_NAME}__toggle-group`}
            type="single"
            defaultValue="1"
            value={appSettings.gameSpeed.toString()}
            aria-label="Text alignment">
            <ToggleGroup.Item
              className={`${CLASS_NAME}__toggle-group-item`}
              value={GAME_SPEED.SLOW.toString()}
              onClick={() => {
                dispatch(AppSettingsActions.updateGameSpeed(GAME_SPEED.SLOW))
                showToast()
              }}
              aria-label="Slow">
              Slow
            </ToggleGroup.Item>
            <ToggleGroup.Item
              className={`${CLASS_NAME}__toggle-group-item`}
              value={GAME_SPEED.NORMAL.toString()}
              onClick={() => {
                dispatch(AppSettingsActions.updateGameSpeed(GAME_SPEED.NORMAL))
                showToast()
              }}
              aria-label="Normal">
              Normal
            </ToggleGroup.Item>
            <ToggleGroup.Item
              className={`${CLASS_NAME}__toggle-group-item`}
              value={GAME_SPEED.FAST.toString()}
              onClick={() => {
                dispatch(AppSettingsActions.updateGameSpeed(GAME_SPEED.FAST))
                showToast()
              }}
              aria-label="Fast">
              Fast
            </ToggleGroup.Item>
          </ToggleGroup.Root>
        </div>
      </div>
      {hostConfig.config.settings.gameAppearanceConfig.hasAnimationSelection ? (
        <div className={`${CLASS_NAME}__option`}>
          <div className={`${CLASS_NAME}__multiline-option`}>
            <label className={`${CLASS_NAME}__label`} htmlFor={'autoplay-switch'}>
              Game Appearance
            </label>
            <CarouselGroup>
              <SelectablePreview
                text="Select All"
                isSelected={allAnimsSelected}
                onSelect={() => {
                  selectAll()
                  showToast()
                }}
              />
              {...generateAnimationPreviews()}
            </CarouselGroup>
          </div>
        </div>
      ) : null}
      <br />
      <section className={`${CLASS_NAME}__section`}>Version: {version}</section>
      <div className={`${CLASS_NAME}__bottom-padding`} />
    </ExpandableDrawer>
  )
}
